import { HugeiconsIcon } from "@hugeicons/react";
import { ElectricPlugsIcon, ReloadIcon } from "@hugeicons-pro/core-bulk-rounded";

import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { BreakdownTable, type Column } from "@/components/dashboard/breakdown-table";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { Skeleton } from "@/components/ui/skeleton";
import { useReference, platformById } from "@/lib/use-reference";
import { compact } from "@/lib/format";
import { getConnectionHealth, type ConnectionHealth } from "@/lib/api/reference";
import type { DataSource } from "@/lib/api/types";
import { METRIC_SOURCES, type MetricSource } from "@/lib/metric-sources";

const STATUS: Record<string, { label: string; variant: "success" | "warning" | "secondary" | "error" }> = {
  connected: { label: "Verbonden", variant: "success" },
  manual: { label: "Handmatig", variant: "secondary" },
  pending: { label: "In opzet", variant: "warning" },
  error: { label: "Fout", variant: "error" },
};
const METHOD: Record<string, string> = { api: "API", csv: "CSV", manual: "Handmatig" };

const HEALTH: Record<ConnectionHealth["health"], { label: string; variant: "success" | "warning" | "error" | "secondary" }> = {
  ok: { label: "Actief", variant: "success" },
  expiring: { label: "Verloopt binnenkort", variant: "warning" },
  expired: { label: "Verlopen", variant: "error" },
  error: { label: "Fout", variant: "error" },
  bridged: { label: "Tijdelijk", variant: "warning" },
};

function reauthLabel(c: ConnectionHealth): string {
  if (c.health === "bridged") return "Zodra Standard access er is";
  if (c.reauth_by === null) return "Geen vervaldatum";
  const d = new Date(c.reauth_by).toLocaleDateString("nl-BE", { day: "numeric", month: "short", year: "numeric" });
  if (c.days_left === null) return d;
  return `${d} · ${c.days_left} d`;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.round(diff / 3_600_000);
  if (h < 1) return "< 1 u geleden";
  if (h < 24) return `${h} u geleden`;
  return `${Math.round(h / 24)} d geleden`;
}

const sourceColumns: Column<MetricSource>[] = [
  { key: "metric", header: "Metric", render: (r) => <span className="font-medium">{r.metric}</span>, exportValue: (r) => r.metric },
  { key: "source", header: "Bron", render: (r) => r.source, exportValue: (r) => r.source },
  { key: "field", header: "API-veld", render: (r) => <span className="font-mono text-xs">{r.field}</span>, exportValue: (r) => r.field },
  { key: "note", header: "Opmerking", render: (r) => <span className="text-muted-foreground">{r.note}</span>, exportValue: (r) => r.note },
];

export function Sources() {
  const { dataSources, platforms, isLoading } = useReference();
  const health = useQuery({ queryKey: ["connection-health"], queryFn: getConnectionHealth, staleTime: 60_000 });
  const loading = isLoading;

  const healthColumns: Column<ConnectionHealth>[] = [
    { key: "platform", header: "Verbinding", render: (c) => (
        <div className="flex items-center gap-2.5">
          <PlatformLogo platform={c.platform_key} size={18} />
          <div className="min-w-0">
            <span className="font-medium">{c.platform_name}</span>
            {/* Only a bridged channel carries this: it names where the numbers
                really come from, so nobody reads them as first-party. */}
            {c.source_note && <p className="text-xs text-muted-foreground">{c.source_note}</p>}
          </div>
        </div>
      ) },
    { key: "health", header: "Toestand", render: (c) => {
        const h = HEALTH[c.health] ?? HEALTH.ok;
        return <Badge variant={h.variant} size="sm">{h.label}</Badge>;
      } },
    { key: "reauth", header: "Opnieuw verbinden vóór", align: "right", render: (c) => (
        <span className={c.health === "expiring" || c.health === "expired" ? "font-medium text-foreground" : "text-muted-foreground"}>
          {reauthLabel(c)}
        </span>
      ) },
    { key: "owner", header: "Wie", render: (c) => <span className="text-muted-foreground">{c.owner}</span> },
    { key: "action", header: "Actie", align: "right", render: (c) =>
        c.reconnect_path ? (
          <a
            href={c.reconnect_path}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted/60"
          >
            <HugeiconsIcon icon={ReloadIcon} size={14} />
            Verbind opnieuw
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">Handmatige sleutel</span>
        ) },
  ];

  const columns: Column<DataSource>[] = [
    { key: "platform", header: "Bron", render: (s) => {
        const p = platformById(platforms, s.platform_id);
        return (
          <div className="flex items-center gap-2.5">
            <PlatformLogo platform={p?.key ?? ""} accentColor={p?.accent_color} size={18} />
            <span className="font-medium">{p?.name ?? "—"}</span>
            {p?.is_aggregator && <Badge variant="outline" size="sm">Aggregator</Badge>}
          </div>
        );
      } },
    { key: "status", header: "Status", render: (s) => {
        const st = STATUS[s.status] ?? STATUS.pending;
        return <Badge variant={st.variant} size="sm">{st.label}</Badge>;
      } },
    { key: "method", header: "Methode", render: (s) => METHOD[s.method] ?? s.method },
    { key: "synced", header: "Laatst gesynct", align: "right", render: (s) => relativeTime(s.last_synced_at) },
    { key: "rows", header: "Rijen", align: "right", render: (s) => compact(s.rows_ingested) },
    { key: "note", header: "Notitie", render: (s) => <span className="text-muted-foreground">{s.note}</span> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Bronnen"
        icon={ElectricPlugsIcon}
        title="Databronnen & ingestie"
        description="Eén bron van waarheid — de status van elke koppeling die het dashboard voedt."
      />

      {loading ? (
        <Skeleton className="h-64 rounded-2xl" />
      ) : (
        <>
          <BreakdownTable
            title="Channels"
            description="De media/ad-platformen die rechtstreeks binnenkomen."
            rows={dataSources.filter((s) => platformById(platforms, s.platform_id)?.kind !== "tool").sort((a, b) => a.status.localeCompare(b.status))}
            columns={columns}
            rowKey={(s) => s.id}
            empty="Nog geen channels geconfigureerd."
          />
          <BreakdownTable
            title="Tools (aggregatie & analytics)"
            description="Voeden het dashboard maar zijn geen channel: GA4 levert de websitebezoekers."
            rows={dataSources.filter((s) => platformById(platforms, s.platform_id)?.kind === "tool").sort((a, b) => a.status.localeCompare(b.status))}
            columns={columns}
            rowKey={(s) => s.id}
            empty="Nog geen tools geconfigureerd."
          />

          {health.data && health.data.length > 0 && (
            <BreakdownTable
              title="Verbindingen & houdbaarheid"
              description="Wanneer elke koppeling opnieuw geautoriseerd moet worden, en wie dat doet. Reageer vóór de vervaldatum om onderbrekingen te vermijden."
              rows={[...health.data].sort((a, b) => {
                const rank = { expired: 0, expiring: 1, error: 2, bridged: 3, ok: 4 } as const;
                if (rank[a.health] !== rank[b.health]) return rank[a.health] - rank[b.health];
                return (a.days_left ?? Infinity) - (b.days_left ?? Infinity);
              })}
              columns={healthColumns}
              rowKey={(c) => c.platform_key}
              empty="Nog geen verbindingen."
            />
          )}

          {/* Hidden for now — re-enable once the wording has been reviewed with Prado. */}
          {false && (
          <BreakdownTable
            title="Metrics & herkomst"
            description="Elk cijfer op het dashboard met de exacte bron en het API-veld, zodat het na te rekenen valt in het platform zelf. Exporteer naar Excel om af te vinken."
            rows={METRIC_SOURCES}
            columns={sourceColumns}
            rowKey={(r) => `${r.metric}-${r.source}-${r.field}`}
            exportName="Prado Pulse — Metrics en herkomst"
          />
          )}
        </>
      )}
    </div>
  );
}
